import React, { useCallback } from "react";
import { useViewTransitionNavigate } from "../hooks/useViewTransitionNavigate";

export interface AppLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  /** Uygulama içi hedef yol. */
  to: string;
  /**
   * Gezinme uygulaması. Verilmezse view-transition'lı varsayılan kullanılır;
   * kabuk bileşenleri kendi navigate'lerini (mobil çekmeceyi kapatan sarmalayıcı
   * gibi) buradan geçirir.
   */
  onNavigate?: (path: string) => void;
  children: React.ReactNode;
}

/** Tarayıcının kendi davranışına bırakılması gereken tıklamalar. */
function isModifiedEvent(e: React.MouseEvent<HTMLAnchorElement>): boolean {
  return e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0;
}

/**
 * Uygulama içi gezinme bağlantısı.
 *
 * Gezinme öğeleri `<button>` iken sağ tıkla "yeni sekmede aç", orta tık, bağlantı
 * kopyalama ve el imleci gibi bir bağlantıdan beklenen her şey yoktu. Burası
 * gerçek bir `<a href>` üretir: değiştirici tuşlu ve orta tıkları tarayıcıya
 * bırakır, sade sol tıkta SPA gezinmesini çalıştırır.
 */
export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(
  function AppLink({ to, onNavigate, onClick, children, ...rest }, ref) {
    const defaultNavigate = useViewTransitionNavigate();
    const navigate = onNavigate ?? defaultNavigate;

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented) return;
        // Yeni sekme / yeni pencere / indir: tarayıcı halletsin.
        if (isModifiedEvent(e)) return;
        e.preventDefault();
        navigate(to);
      },
      [onClick, navigate, to],
    );

    return (
      <a ref={ref} href={to} onClick={handleClick} {...rest}>
        {children}
      </a>
    );
  },
);

export default AppLink;
